/**
 * Desktop switching orchestration — "Meus Desktops"
 *
 * Rules (spec: docs/features/meus-desktops-prompt.md):
 * - Switching NEVER discards client changes: the active desktop is snapshotted
 *   into its local cache before the next one loads.
 * - A DIRTY local copy wins over the server on load; a clean one is refetched
 *   (fresh copy in case it changed on another machine).
 * - The server is only touched on explicit save or "Recarregar do servidor".
 *
 * NOTE: no static import of chartStore here — chartStore imports this module
 * for boot restore, so callers pass loadCharts/chartsStore in (same convention
 * as core/serialization.js).
 */

import useWorkspaceStore, {
  getWorkspaceContext,
} from "../store/workspaceStore.jsx";
import logger from "../utils/logger";
import { deserializeWorkspace } from "./core/serialization.js";
import { autoSaveWorkspace } from "./storage/localStorage.js";
import { loadDesktopRemote } from "./storage/remoteStorage.js";
import {
  saveDesktopToCache,
  getDesktopCache,
  isDesktopCacheDirty,
  captureDesktopBaseline,
  clearDesktopCache,
} from "./storage/desktopCache.js";

/**
 * Comparison symbols are restored ~500ms after loadCharts (see
 * deserializeWorkspace) — the clean baseline must be captured after they land,
 * or the desktop would flag dirty right after loading.
 */
const BASELINE_CAPTURE_DELAY_MS = 1500;

/**
 * While a desktop is being restored (fetch + deserialize + baseline), the
 * live state is PARTIAL — comparing it against any baseline reads as a
 * spurious "dirty" until the next poll. Dirty consumers (bar, dialog) must
 * skip the live check inside this window.
 */
let _settlingDesktopId = null;

const beginRestoreSettle = (desktopId) => {
  _settlingDesktopId = desktopId;
  // Fallback for paths without a scheduled baseline capture (cache loads)
  setTimeout(() => {
    if (_settlingDesktopId === desktopId) _settlingDesktopId = null;
  }, BASELINE_CAPTURE_DELAY_MS + 1500);
};

/**
 * @param {string|number} id - Desktop id
 * @returns {boolean} - True while this desktop's restore is still settling
 */
export const isDesktopRestoreSettling = (id) =>
  _settlingDesktopId != null && _settlingDesktopId === id;

/**
 * Capture the clean baseline once restoration settles. Guarded: skipped if the
 * user already switched to another desktop meanwhile.
 * @param {Function} getChartsState - () => charts store state
 * @param {string|number} desktopId - Desktop this baseline belongs to
 */
const scheduleBaselineCapture = (getChartsState, desktopId) => {
  setTimeout(() => {
    if (_settlingDesktopId === desktopId) _settlingDesktopId = null;
    const { user, broker } = getWorkspaceContext();
    if (useWorkspaceStore.getState().currentDesktopId !== desktopId) return;
    captureDesktopBaseline(getChartsState(), user, broker, desktopId);
  }, BASELINE_CAPTURE_DELAY_MS);
};

/**
 * Snapshot the ACTIVE desktop to its local cache (or to the legacy auto-save
 * slot when it's an unsaved "Novo desktop"). Synchronous — safe in
 * beforeunload. Replaces the plain autoSaveWorkspace call sites.
 * @param {Object} chartsStore - Charts store state
 * @param {string} user - Username/email
 * @param {string} broker - Broker identifier
 * @returns {boolean} - Success
 */
export const persistActiveDesktop = (chartsStore, user, broker) => {
  const desktopId = useWorkspaceStore.getState().currentDesktopId;
  if (desktopId != null) {
    return saveDesktopToCache(chartsStore, user, broker, desktopId);
  }
  return autoSaveWorkspace(chartsStore, user, broker);
};

/**
 * Switch to a desktop: persist the current one, then load the target from its
 * dirty local copy (local wins) or from the server (fresh, resets baseline).
 * No confirmation — the save-on-switch makes it safe.
 * @param {string|number} id - Target desktop id (server chartid)
 * @param {string} name - Target desktop name
 * @param {Object} deps
 * @param {Function} deps.getChartsState - () => charts store state
 * @param {Function} deps.loadCharts - clear + load action (wrappedLoadAction)
 * @returns {Promise<boolean>} - Success
 */
export const switchToDesktop = async (
  id,
  name,
  { getChartsState, loadCharts },
) => {
  const { user, broker } = getWorkspaceContext();
  const workspaceStore = useWorkspaceStore.getState();

  // 1. Persist the desktop being left (never lose client changes)
  persistActiveDesktop(getChartsState(), user, broker);

  // 2. Load the target — dirty local copy wins over the server
  beginRestoreSettle(id);
  const entry = getDesktopCache(user, broker, id);
  if (entry && isDesktopCacheDirty(entry)) {
    const success = deserializeWorkspace(entry.snapshot, loadCharts);
    if (!success) {
      // Corrupt local copy: fall back to the server rather than a dead app
      logger.workspace.warning(
        `Cópia local de "${name}" inválida — recarregando do servidor`,
        { id },
      );
      clearDesktopCache(user, broker, id);
      await loadDesktopRemote(id, loadCharts);
      scheduleBaselineCapture(getChartsState, id);
    } else {
      logger.workspace.success(
        `Desktop "${name}" carregado (cópia local com modificações)`,
        { id },
      );
    }
  } else {
    await loadDesktopRemote(id, loadCharts);
    scheduleBaselineCapture(getChartsState, id);
  }

  // 3. Track the new active desktop (persisted → app reopens on it)
  workspaceStore.setCurrentDesktopId(id);
  if (name) workspaceStore.setDesktopName(name);
  return true;
};

/**
 * "Recarregar do servidor": discard the local copy and refetch. Caller is
 * responsible for user confirmation (this destroys local work).
 * @param {string|number} id - Desktop id
 * @param {string} name - Desktop name
 * @param {Object} deps - { getChartsState, loadCharts }
 * @returns {Promise<boolean>} - Success
 */
export const reloadDesktopFromServer = async (
  id,
  name,
  { getChartsState, loadCharts },
) => {
  const { user, broker } = getWorkspaceContext();
  clearDesktopCache(user, broker, id);

  // Only reload the workspace if the discarded desktop is the active one
  if (useWorkspaceStore.getState().currentDesktopId === id) {
    beginRestoreSettle(id);
    await loadDesktopRemote(id, loadCharts);
    scheduleBaselineCapture(getChartsState, id);
    if (name) useWorkspaceStore.getState().setDesktopName(name);
  }
  return true;
};

/**
 * Mark the active desktop as server-synced (call right after a successful
 * remote save): the current state becomes the clean baseline.
 * @param {Object} chartsStore - Charts store state
 * @param {string|number} id - Desktop id that was saved
 */
export const markDesktopSavedToServer = (chartsStore, id) => {
  if (id == null) return;
  const { user, broker } = getWorkspaceContext();
  captureDesktopBaseline(chartsStore, user, broker, id);
  const workspaceStore = useWorkspaceStore.getState();
  if (workspaceStore.currentDesktopId !== id) {
    workspaceStore.setCurrentDesktopId(id);
  }
};

/**
 * Boot restore: reopen the app on the persisted selected desktop.
 * Local cache first (the living copy), server as fallback.
 * @param {Function} getChartsState - () => charts store state
 * @param {Function} loadCharts - Load action from chartStore
 * @returns {Promise<boolean>} - True if a desktop was restored
 */
export const restoreSelectedDesktopOnBoot = async (
  getChartsState,
  loadCharts,
) => {
  const desktopId = useWorkspaceStore.getState().currentDesktopId;
  if (desktopId == null) return false;

  const { user, broker } = getWorkspaceContext();
  beginRestoreSettle(desktopId);
  const entry = getDesktopCache(user, broker, desktopId);
  if (entry) {
    const success = deserializeWorkspace(entry.snapshot, loadCharts);
    if (success) {
      console.log(
        `[DesktopManager] Boot: restored desktop ${desktopId} from local cache`,
      );
      return true;
    }
    clearDesktopCache(user, broker, desktopId);
  }

  try {
    await loadDesktopRemote(desktopId, loadCharts);
    scheduleBaselineCapture(getChartsState, desktopId);
    return true;
  } catch (error) {
    // Desktop may have been deleted on another machine — fall back to the
    // legacy startup flow (caller decides), don't block the app
    console.warn(
      `[DesktopManager] Boot: failed to restore desktop ${desktopId} from server:`,
      error,
    );
    useWorkspaceStore.getState().setCurrentDesktopId(null);
    return false;
  }
};
